import React, { useEffect } from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { DashBoardParamList, DispatchParamList } from '../../../typings/Params';
import Filters from '../../Filter/FilterIndex/FilterIndex';
import FilterInput from '../../Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { useState } from 'react';
import TypeDropdown from './TypeDropdown';
import PriorityList from './PriorityList';
import StatusList from './StatusList';
import TagsList from './TagsList';
import ActingBodiesList from './ActingBodiesList';
import moment from 'moment';
import { TouchableOpacity, View } from 'react-native';
import MyAppText from '../../MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import LocationSelector from '../../LocationSelector';
import { centralAPI } from '../../../services/central-api';
import { Slider } from '@miblanchard/react-native-slider';
import Map from '../../Map';
import { useAuth } from '../../../context/Auth';
import { OccurrencePriorities, OccurrenceSituation, TriggerType } from '../../../schemas/dispatch';
import ActingBodyUnitsList from './ActingBodyUnitsList';
import CameraTagsList from './CameraTagsList';
import CollapsibleFilter from './CollapsibleFilter';
import DispatchUnitNamesList from './DispatchUnitNamesList';
import FilterRadioList from '../FilterRadioList';
import DispatchActingBodiesCommandsList from './DispatchActingBodiesCommandsList';
import DispatchGenericUserList from './DispatchGenericUserList';
import DispatchDisplacedReasonsList from './DispatchDisplacedReasonsList';
import FinishReasonSubCategoryList from './FinishReasonSubcategoryList';
import FinishReasonList from './FinishReasonList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import FilterPeriodSelector from './FilterPeriodSelector';
import FilterDependencyMessage from '../FilterDependencyMessage';
import Tooltip from '../../Tooltip';
import AttachmentTypesList, { AttachmentType } from './AttachmentTypesList';
import InvolvedPeopleList from './InvolvedPeopleList';
import OccurrenceTypesList from './OccurrenceTypesList';
import RegionsList from '../RegionsList';


interface Props {
    filters: DispatchParamList['Occurrences'] | DashBoardParamList['DashboardOccurrences'];
    setFilters: React.Dispatch<React.SetStateAction<DispatchParamList['Occurrences'] | DashBoardParamList['DashboardOccurrences']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function OccurrencesFilter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<DispatchParamList, 'Occurrences'>>();
    const { styles, theme } = useStyles(styleSheet);

    const [selectedTriggerTypeFilter, setSelectedTriggerTypeFilter] = useState<(TriggerType | 'manual')[]>(filters.triggerTypeIds ?? []);
    const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<OccurrencePriorities | 'all'>(filters.priority ?? 'all');
    const [selectedSituationFilter, setSelectedSituationFilter] = useState<OccurrenceSituation | 'all'>(filters.situation ?? 'all');
    const [selectedOccurrenceTypesFilter, setSelectedOccurrenceTypesFilter] = useState<number[]>(filters.occurrenceTypeId ?? []);
    const [selectedUnitNamesIds, setSelectedUnitNamesIds] = useState<number[]>(filters.unitNamesIds ?? []);
    const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>(filters.tags ?? []);
    const [selectedCameraTagsIds, setSelectedCameraTagsIds] = useState<number[]>(filters.cameraTags ?? []);
    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [selectedMapPolygonsIds, setSelectedMapPolygonsIds] = useState<number[]>(filters.mapPolygonIds ?? []);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType || '');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);

    const [selectedAssumedBy, setSelectedAssumedBy] = useState<number>(filters.assumedBy ?? 0);
    const [selectedTransferredBy, setSelectedTransferredBy] = useState<number>(filters.transferredBy ?? 0);
    const [selectedReceivedBy, setSelectedReceivedBy] = useState<number>(filters.receivedBy ?? 0);
    const [selectedFinishedBy, setSelectedFinishedBy] = useState<number>(filters.finishedBy ?? 0);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [actingBodyCommandIds, setActingBodyCommandIds] = useState<number[]>(filters.actingBodyCommandIds ?? []);
    const [withDispatch, setWithDispatch] = useState<boolean>(filters.withDispatch ?? false);
    const [withoutDispatch, setWithoutDispatch] = useState<boolean>(filters.withoutDispatch ?? false);
    const [withOccurrenceDocument, setWithOccurrenceDocument] = useState<boolean>(filters.withOccurrenceDocument ?? false);
    const [withoutOccurrenceDocument, setWithoutOccurrenceDocument] = useState<boolean>(filters.withoutOccurrenceDocument ?? false);
    const [selectedDisplaceReasonId, setSelectedDisplaceReasonId] = useState<number[]>(filters.displaceReasonIds ?? []);
    const [selectedFinishReasonCategoryIds, setSelectedFinishReasonCategoryIds] = useState<number[]>(filters.finishReasonCategoryIds ?? []);
    const [selectedRadius, setSelectedRadius] = useState<number>(filters.radius ?? 1);
    const [address, setAddress] = useState('');
    const [pin, setPin] = useState<LatLng | undefined>(filters.latitude && filters.longitude ? { lat: filters.latitude, lng: filters.longitude } : undefined);
    const [selectedFinishReasonSubCategoryIds, setSelectedFinishReasonSubCategoryIds] = useState<number[]>(filters.finishReasonSubCategoryIds ?? []);
    const [selectedActingBodyUnitIds, setSelectedActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [policeReportFilter, setPoliceReportFilter] = useState(filters.policeReport ?? '');
    const [openFilter, setOpenFilter] = useState<string | null>(null);
    const [selectedPreset, setSelectedPreset] = useState<string>('');
    const actingBodyId = getActingBodyId();
    const needsActingBody = actingBodyId === 0;
    const [selectedAttachmentType, setSelectedAttachmentType] = useState<AttachmentType | 'all'>(
        filters.attachmentTypes?.[0] ?? 'all'
    );
    const [involvedPersonSearch, setInvolvedPersonSearch] = useState(filters.involvedPersonSearch ?? '');
    const [handcuffUsed, setHandcuffUsed] = useState<boolean>(filters.handcuffUsed ?? false);
    const [arrested, setArrested] = useState<boolean>(filters.arrested ?? false);
    const [expandedFinishCategory, setExpandedFinishCategory] = useState<number>(0);

    const totalActiveFilters = [
        selectedActingBodyId !== 0 ? 1 : 0,
        selectedUnitNamesIds.length > 0 ? 1 : 0,
        actingBodyCommandIds.length > 0 ? 1 : 0,
        (beginInDateFilter || endInDateFilter) ? 1 : 0,
        selectedPriorityFilter !== 'all' ? 1 : 0,
        selectedSituationFilter !== 'all' ? 1 : 0,
        selectedTagsIds.length > 0 ? 1 : 0,
        policeReportFilter ? 1 : 0,
        selectedActingBodyUnitIds.length > 0 ? 1 : 0,
        selectedDisplaceReasonId.length > 0 ? 1 : 0,
        selectedMapPolygonsIds.length > 0 ? 1 : 0,
        pin ? 1 : 0,
        (withDispatch || withoutDispatch) ? 1 : 0,
        (withOccurrenceDocument || withoutOccurrenceDocument) ? 1 : 0,
        selectedTriggerTypeFilter.length > 0 ? 1 : 0,
        selectedOccurrenceTypesFilter.length > 0 ? 1 : 0,
        selectedCameraTagsIds.length > 0 ? 1 : 0,
        selectedAssumedBy !== 0 ? 1 : 0,
        selectedFinishedBy !== 0 ? 1 : 0,
        selectedReceivedBy !== 0 ? 1 : 0,
        selectedTransferredBy !== 0 ? 1 : 0,
        selectedFinishReasonCategoryIds.length > 0 ? 1 : 0,
        selectedFinishReasonSubCategoryIds.length > 0 ? 1 : 0,
        selectedAttachmentType !== 'all' ? 1 : 0,
        (involvedPersonSearch || handcuffUsed || arrested) ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    const toggleFilter = (key: string) => {
        setOpenFilter(prev => prev === key ? null : key);
    };

    useEffect(() => {
        if (actingBodyId) {
            setSelectedActingBodyUnitIds([]);
            setActingBodyCommandIds([]);
            setSelectedTagsIds([]);
            setSelectedUnitNamesIds([]);
            setSelectedOccurrenceTypesFilter([]);
            setSelectedAssumedBy(0);
            setSelectedFinishedBy(0);
            setSelectedReceivedBy(0);
            setSelectedTransferredBy(0);
            setSelectedFinishReasonCategoryIds([]);
            setSelectedFinishReasonSubCategoryIds([]);
        }
    }, [actingBodyId]);

    const handleCleanFilter = () => {
        setOpenFilter(null);
        setTextFilter('');
        setSelectedOccurrenceTypesFilter([]);
        setSelectedActingBodyId(0);
        setSelectedMapPolygonsIds([]);
        setSelectedAssumedBy(0);
        setSelectedTransferredBy(0);
        setSelectedReceivedBy(0);
        setSelectedFinishedBy(0);
        setSelectedPriorityFilter('all');
        setSelectedSituationFilter('all');
        setSelectedTriggerTypeFilter([]);
        setSelectedUnitNamesIds([]);
        setSelectedTagsIds([]);
        setSelectedCameraTagsIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setActingBodyCommandIds([]);
        setWithDispatch(false);
        setWithoutDispatch(false);
        setWithOccurrenceDocument(false);
        setWithoutOccurrenceDocument(false);
        setSelectedDisplaceReasonId([]);
        setSelectedFinishReasonCategoryIds([]);
        setSelectedRadius(1);
        setAddress('');
        setPin(undefined);
        setSelectedPolygonType('');
        setSelectedFinishReasonSubCategoryIds([]);
        setSelectedActingBodyUnitIds([]);
        setPoliceReportFilter('');
        setSelectedAttachmentType('all');
        setInvolvedPersonSearch('');
        setHandcuffUsed(false);
        setArrested(false);
        setExpandedFinishCategory(0);
        setSelectedPreset('');
    };

    async function fetchAddress(pin: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pin.lat, longitude: pin.lng });
        if (address) {
            setAddress(address);
        }
    }

    useEffect(() => {
        if (pin) {
            fetchAddress(pin);
        }
    }, [pin]);

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            situation: selectedSituationFilter !== 'all' ? selectedSituationFilter : undefined,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerTypeIds: selectedTriggerTypeFilter.length ? selectedTriggerTypeFilter : undefined,
            occurrenceTypeId: selectedOccurrenceTypesFilter.length ? selectedOccurrenceTypesFilter : undefined,
            unitNamesIds: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined,
            textFilter: textFilter ? textFilter : undefined,
            page: 0,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
            assumedBy: selectedAssumedBy !== 0 ? selectedAssumedBy : undefined,
            transferredBy: selectedTransferredBy !== 0 ? selectedTransferredBy : undefined,
            receivedBy: selectedReceivedBy !== 0 ? selectedReceivedBy : undefined,
            finishedBy: selectedFinishedBy !== 0 ? selectedFinishedBy : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : 0,
            endIn: endIn && !isNaN(endIn) ? endIn : 0,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            withOccurrenceDocument: withOccurrenceDocument ? withOccurrenceDocument : undefined,
            withoutOccurrenceDocument: withoutOccurrenceDocument ? withoutOccurrenceDocument : undefined,
            displaceReasonIds: selectedDisplaceReasonId.length ? selectedDisplaceReasonId : undefined,
            finishReasonCategoryIds: selectedFinishReasonCategoryIds.length ? selectedFinishReasonCategoryIds : undefined,
            latitude: pin ? pin.lat : undefined,
            longitude: pin ? pin.lng : undefined,
            radius: pin ? selectedRadius : undefined,
            finishReasonSubCategoryIds: selectedFinishReasonSubCategoryIds.length ? selectedFinishReasonSubCategoryIds : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length ? selectedActingBodyUnitIds : undefined,
            policeReport: policeReportFilter ? policeReportFilter : undefined,
            cameraTags: selectedCameraTagsIds.length ? selectedCameraTagsIds : undefined,
            attachmentTypes: selectedAttachmentType !== 'all' ? [selectedAttachmentType] : undefined,
            involvedPersonSearch: involvedPersonSearch ? involvedPersonSearch : undefined,
            handcuffUsed: handcuffUsed ? handcuffUsed : undefined,
            arrested: arrested ? arrested : undefined,
        });

        navigation.setParams({
            situation: selectedSituationFilter !== 'all' ? selectedSituationFilter : undefined,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerTypeIds: selectedTriggerTypeFilter.length ? selectedTriggerTypeFilter : undefined,
            occurrenceTypeId: selectedOccurrenceTypesFilter.length ? selectedOccurrenceTypesFilter : undefined,
            unitNamesIds: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined,
            textFilter: textFilter ? textFilter : undefined,
            page: 1,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
            assumedBy: selectedAssumedBy !== 0 ? selectedAssumedBy : undefined,
            transferredBy: selectedTransferredBy !== 0 ? selectedTransferredBy : undefined,
            receivedBy: selectedReceivedBy !== 0 ? selectedReceivedBy : undefined,
            finishedBy: selectedFinishedBy !== 0 ? selectedFinishedBy : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : 0,
            endIn: endIn && !isNaN(endIn) ? endIn : 0,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            withOccurrenceDocument: withOccurrenceDocument ? withOccurrenceDocument : undefined,
            withoutOccurrenceDocument: withoutOccurrenceDocument ? withoutOccurrenceDocument : undefined,
            displaceReasonIds: selectedDisplaceReasonId.length ? selectedDisplaceReasonId : undefined,
            finishReasonCategoryIds: selectedFinishReasonCategoryIds.length ? selectedFinishReasonCategoryIds : undefined,
            latitude: pin ? pin.lat : undefined,
            longitude: pin ? pin.lng : undefined,
            radius: pin ? selectedRadius : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length ? selectedActingBodyUnitIds : undefined,
            policeReport: policeReportFilter ? policeReportFilter : undefined,
            cameraTags: selectedCameraTagsIds.length ? selectedCameraTagsIds : undefined,
            attachmentTypes: selectedAttachmentType !== 'all' ? [selectedAttachmentType] : undefined,
            involvedPersonSearch: involvedPersonSearch ? involvedPersonSearch : undefined,
            handcuffUsed: handcuffUsed ? handcuffUsed : undefined,
            arrested: arrested ? arrested : undefined,
        });

    };

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_occurrences) {
            return selectedActingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={!!selectedPolygonType && !selectedMapPolygonsIds.length}
            filterCount={totalActiveFilters}
        >
            <MyAppText style={styles.sectionTitle(0)}>
                {translate('organizational')}
            </MyAppText>
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences) &&
                <CollapsibleFilter
                    label={translate('actingBody')}
                    isOpen={openFilter === 'actingBody'}
                    onToggle={() => toggleFilter('actingBody')}
                    count={selectedActingBodyId !== 0 ? 1 : 0}
                >
                    <ActingBodiesList value={selectedActingBodyId} setValue={setSelectedActingBodyId} />
                </CollapsibleFilter>
            }

            <CollapsibleFilter
                label={translate('actingBodyUnit')}
                isOpen={openFilter === 'unitNames'}
                onToggle={() => toggleFilter('unitNames')}
                count={selectedActingBodyUnitIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <ActingBodyUnitsList value={selectedActingBodyUnitIds} setValue={setSelectedActingBodyUnitIds} actingBodyId={actingBodyId} />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('command')}
                isOpen={openFilter === 'actingBodyCommands'}
                onToggle={() => toggleFilter('actingBodyCommands')}
                count={actingBodyCommandIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DispatchActingBodiesCommandsList value={actingBodyCommandIds} actingBodyId={actingBodyId} setValue={setActingBodyCommandIds} />
                }
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('occurrenceData')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('period')}
                isOpen={openFilter === 'period'}
                onToggle={() => toggleFilter('period')}
                count={(selectedPreset || beginInDateFilter || endInDateFilter) ? 1 : 0}
            >
                <FilterPeriodSelector
                    preset={selectedPreset}
                    setPreset={(preset, range) => {
                        setSelectedPreset(preset);
                        if (range) {
                            setBeginInDateFilter(new Date(range.beginIn));
                            setBeginInHourFilter(moment(range.beginIn).format('HH:mm'));
                            setEndInDateFilter(new Date(range.endIn));
                            setEndInHourFilter(moment(range.endIn).format('HH:mm'));
                        } else if (preset === '') {
                            setBeginInDateFilter(null);
                            setBeginInHourFilter('');
                            setEndInDateFilter(null);
                            setEndInHourFilter('');
                        }
                    }}
                    beginDate={beginInDateFilter}
                    setBeginDate={setBeginInDateFilter}
                    beginTime={beginInHourFilter}
                    setBeginTime={setBeginInHourFilter}
                    endDate={endInDateFilter}
                    setEndDate={setEndInDateFilter}
                    endTime={endInHourFilter}
                    setEndTime={setEndInHourFilter}
                    onClear={() => {
                        setSelectedPreset('');
                        setBeginInDateFilter(null);
                        setBeginInHourFilter('');
                        setEndInDateFilter(null);
                        setEndInHourFilter('');
                    }}
                    clearPreset={() => setSelectedPreset('')}
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('priority')}
                isOpen={openFilter === 'priority'}
                onToggle={() => toggleFilter('priority')}
                count={selectedPriorityFilter !== 'all' ? 1 : 0}
            >
                <PriorityList value={selectedPriorityFilter} setValue={setSelectedPriorityFilter} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('status')}
                isOpen={openFilter === 'status'}
                onToggle={() => toggleFilter('status')}
                count={selectedSituationFilter !== 'all' ? 1 : 0}
            >
                <StatusList value={selectedSituationFilter} setValue={setSelectedSituationFilter} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('tag')}
                isOpen={openFilter === 'tags'}
                onToggle={() => toggleFilter('tags')}
                count={selectedTagsIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <TagsList value={selectedTagsIds} setValue={setSelectedTagsIds} actingBodyId={getActingBodyId()} />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('policeReport')}
                isOpen={openFilter === 'policeReport'}
                onToggle={() => toggleFilter('policeReport')}
                count={policeReportFilter ? 1 : 0}
                searchComponent={<FilterInput
                    placeholder={translate('typeToSearch')}
                    value={policeReportFilter}
                    onChange={text => setPoliceReportFilter(text)}
                />}
            />

            <MyAppText style={styles.sectionTitle()}>
                {translate('teams')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('team')}
                isOpen={openFilter === 'teams'}
                onToggle={() => toggleFilter('teams')}
                count={selectedUnitNamesIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DispatchUnitNamesList value={selectedUnitNamesIds} setValue={setSelectedUnitNamesIds} actingBodyId={getActingBodyId()} />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('displaced')}
                isOpen={openFilter === 'displaceReasons'}
                onToggle={() => toggleFilter('displaceReasons')}
                count={selectedDisplaceReasonId.length}
            >
                <DispatchDisplacedReasonsList setValue={setSelectedDisplaceReasonId} value={selectedDisplaceReasonId} />
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('additionalInformation')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('attachment')}
                isOpen={openFilter === 'attachment'}
                onToggle={() => toggleFilter('attachment')}
                count={selectedAttachmentType !== 'all' ? 1 : 0}
            >
                <AttachmentTypesList value={selectedAttachmentType} setValue={setSelectedAttachmentType} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('involvedPerson')}
                isOpen={openFilter === 'involvedPerson'}
                onToggle={() => toggleFilter('involvedPerson')}
                count={(involvedPersonSearch ? 1 : 0) + (handcuffUsed ? 1 : 0) + (arrested ? 1 : 0)}
            >
                <InvolvedPeopleList
                    search={involvedPersonSearch}
                    setSearch={setInvolvedPersonSearch}
                    handcuffUsed={handcuffUsed}
                    setHandcuffUsed={setHandcuffUsed}
                    arrested={arrested}
                    setArrested={setArrested}
                />
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('location')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('region')}
                isOpen={openFilter === 'regions'}
                onToggle={() => toggleFilter('regions')}
                count={selectedMapPolygonsIds.length}
            >
                <RegionsList
                    multiple={true}
                    polygonType={selectedPolygonType}
                    setPolygonType={setSelectedPolygonType}
                    mapPolygonsIds={selectedMapPolygonsIds}
                    setMapPolygonsIds={setSelectedMapPolygonsIds}
                    setPendingLoading={setPolygonPendingLoading}
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('findByLocation')}
                isOpen={openFilter === 'findByLocation'}
                onToggle={() => toggleFilter('findByLocation')}
                count={pin ? 1 : 0}
                searchComponent={
                    pin ? (
                        <Tooltip
                            useSimpleBox
                            tooltipContent={translate('onlyOneAddressAtATime')}
                            tooltipDirection='top'
                            style={{
                                color: theme.colors.white['500'],
                                padding: 16,
                                borderRadius: 12,
                                width: 200,
                                justifyContent: 'center',
                                zIndex: 9999,
                                background: theme.colors.neutral['500'],
                            }}
                        >
                            <LocationSelector
                                address={address}
                                onAddressChange={setAddress}
                                onPinChange={setPin}
                                disabled
                            />
                        </Tooltip>
                    ) : (
                        <LocationSelector address={address} onAddressChange={setAddress} onPinChange={setPin} />
                    )
                }
            >
                <View style={{ gap: 12 }}>
                    {pin && (
                        <>
                            <View style={styles.headerRow}>
                                <MyAppText style={styles.headerLabel}>{translate('selectedAddress')}</MyAppText>
                                <TouchableOpacity onPress={() => {
                                    setPin(undefined); setAddress('');
                                }}>
                                    <MyAppText style={styles.clearSelection}>{translate('clearSelection')}</MyAppText>
                                </TouchableOpacity>
                            </View>

                            <View style={styles.addressBadge}>
                                <MyAppText style={styles.addressText}>{address}</MyAppText>
                                <TouchableOpacity onPress={() => {
                                    setPin(undefined); setAddress('');
                                }}>
                                    <FontAwesomeIcon icon={faXmark as IconDefinition} fontSize={14} color={theme.colors.labelColor} />
                                </TouchableOpacity>
                            </View>

                            <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                <MyAppText style={{ fontWeight: '600' }}>{translate('radius')}</MyAppText>
                                <MyAppText>{`: ${selectedRadius.toFixed(2)}km`}</MyAppText>
                            </View>
                            <Slider
                                animateTransitions
                                minimumValue={0.1}
                                maximumValue={3}
                                minimumTrackTintColor={theme.colors.primary['500']}
                                renderThumbComponent={() => (
                                    <View style={styles.thumb} />
                                )}
                                maximumTrackTintColor={theme.colors.primary['200']}
                                value={selectedRadius}
                                step={0.1}
                                onValueChange={(value) => setSelectedRadius(value[0])}
                            />
                            <View style={{ width: '100%', height: 450 }}>
                                <Map pinnedMarker={pin} draggable={true} mapControls={false} radius={selectedRadius} />
                            </View>
                        </>
                    )}
                </View>
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('classification')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('dispatchInOccurrence')}
                isOpen={openFilter === 'withDispatch'}
                onToggle={() => toggleFilter('withDispatch')}
                count={(withDispatch || withoutDispatch) ? 1 : 0}
            >
                <FilterRadioList
                    items={[
                        { label: translate('withTeamDispatch'), value: 'yes' },
                        { label: translate('withoutTeamDispatch'), value: 'no' },
                    ]}
                    value={withDispatch ? 'yes' : withoutDispatch ? 'no' : 'all'}
                    setValue={(value) => {
                        setWithDispatch(value === 'yes');
                        setWithoutDispatch(value === 'no');
                    }}
                    clearValue='all'
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('occurrenceDocument')}
                isOpen={openFilter === 'withOccurrenceDocument'}
                onToggle={() => toggleFilter('withOccurrenceDocument')}
                count={(withOccurrenceDocument || withoutOccurrenceDocument) ? 1 : 0}
            >
                <FilterRadioList
                    items={[
                        { label: translate('withOccurrenceDocumentGenerated'), value: 'yes' },
                        { label: translate('withoutOccurrenceDocumentGenerated'), value: 'no' },
                    ]}
                    value={withOccurrenceDocument ? 'yes' : withoutOccurrenceDocument ? 'no' : 'all'}
                    setValue={(value) => {
                        setWithOccurrenceDocument(value === 'yes');
                        setWithoutOccurrenceDocument(value === 'no');
                    }}
                    clearValue='all'
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('typeOfIntegration')}
                isOpen={openFilter === 'type'}
                onToggle={() => toggleFilter('type')}
                count={selectedTriggerTypeFilter.length}
            >
                <TypeDropdown value={selectedTriggerTypeFilter} setValue={setSelectedTriggerTypeFilter} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('occurrenceType')}
                isOpen={openFilter === 'occurrenceType'}
                onToggle={() => toggleFilter('occurrenceType')}
                count={selectedOccurrenceTypesFilter.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <OccurrenceTypesList value={selectedOccurrenceTypesFilter} setValue={setSelectedOccurrenceTypesFilter} actingBodyId={actingBodyId} />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('cameraTag')}
                isOpen={openFilter === 'cameraTags'}
                onToggle={() => toggleFilter('cameraTags')}
                count={selectedCameraTagsIds.length}
            >
                <CameraTagsList value={selectedCameraTagsIds} setValue={setSelectedCameraTagsIds} />
            </CollapsibleFilter>


            <MyAppText style={styles.sectionTitle()}>
                {translate('occurrenceActions')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('assumedBy')}
                isOpen={openFilter === 'assumedBy'}
                onToggle={() => toggleFilter('assumedBy')}
                count={selectedAssumedBy !== 0 ? 1 : 0}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DispatchGenericUserList value={selectedAssumedBy} setValue={setSelectedAssumedBy} actingBodyId={actingBodyId} label='assumedBy' />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('finishedBy')}
                isOpen={openFilter === 'finishedBy'}
                onToggle={() => toggleFilter('finishedBy')}
                count={selectedFinishedBy !== 0 ? 1 : 0}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DispatchGenericUserList value={selectedFinishedBy} setValue={setSelectedFinishedBy} actingBodyId={actingBodyId} label='finishedBy' />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('receivedBy')}
                isOpen={openFilter === 'receivedBy'}
                onToggle={() => toggleFilter('receivedBy')}
                count={selectedReceivedBy !== 0 ? 1 : 0}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DispatchGenericUserList value={selectedReceivedBy} setValue={setSelectedReceivedBy} actingBodyId={actingBodyId} label='receivedBy' />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('transferredBy')}
                isOpen={openFilter === 'transferredBy'}
                onToggle={() => toggleFilter('transferredBy')}
                count={selectedTransferredBy !== 0 ? 1 : 0}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DispatchGenericUserList value={selectedTransferredBy} setValue={setSelectedTransferredBy} actingBodyId={actingBodyId} label='transferredBy' />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('finishReason')}
                isOpen={openFilter === 'finishReason'}
                onToggle={() => toggleFilter('finishReason')}
                count={selectedFinishReasonCategoryIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <FinishReasonList value={selectedFinishReasonCategoryIds} setValue={setSelectedFinishReasonCategoryIds} actingBodyId={actingBodyId} onSelectCategory={() => setSelectedFinishReasonSubCategoryIds([])} />
                }
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('finishReasonSubCategory')}
                isOpen={openFilter === 'finishReasonSubCategory'}
                onToggle={() => toggleFilter('finishReasonSubCategory')}
                count={selectedFinishReasonSubCategoryIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <FinishReasonSubCategoryList
                        value={selectedFinishReasonSubCategoryIds}
                        setValue={setSelectedFinishReasonSubCategoryIds}
                        actingBodyId={actingBodyId}
                        expandedCategory={expandedFinishCategory}
                        setExpandedCategory={setExpandedFinishCategory}
                        onSelectSubCategory={() => setSelectedFinishReasonCategoryIds([])}
                    />}
            </CollapsibleFilter>
        </Filters >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    filterLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
    sectionTitle: (padding: number = 16) => ({
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        paddingTop: padding,
        textTransform: 'uppercase',
    }),
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    addressBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        backgroundColor: theme.colors.primary['200'],
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 20,
    },
    addressText: {
        flex: 1,
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    thumb: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: theme.colors.white['500'],
        borderWidth: 5,
        borderColor: theme.colors.primary['500'],
    },
}));


export default OccurrencesFilter;
